import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import Swal from 'sweetalert2';

import {
  ChangePasswordPayload,
  ForgotPayload,
  LoginPayload,
  LoginResponse,
  RememberMeLoginPayload,
  ResetPasswordPayload,
  VerifyOtpPayload,
  VerifyOtpResponse
} from '../models/auth.model';
import { CommonResponse } from '../models/common.model';
import { receivedNotificationList } from '../models/notification.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  emailSubject: BehaviorSubject<string | null> = new BehaviorSubject<
    string | null
  >(null);

  profileDetails: BehaviorSubject<LoginResponse | null> =
    new BehaviorSubject<LoginResponse | null>(null);

  resetTokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');

  deviceTokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(
    'test'
  );

  private isOpenSubject = new BehaviorSubject<boolean>(false);
  isOpen$ = this.isOpenSubject.asObservable();
  toggleSidebarView(): void {
    this.isOpenSubject.next(!this.isOpenSubject.value);
  }

  constructor(
    private readonly http: HttpClient,
    private _translateService: TranslateService
  ) {}

  isAuthenticated(): boolean {
    if (
      localStorage.getItem(environment.storageKey) &&
      localStorage.getItem(environment.storageKey) !== null
    ) {
      return true;
    } else {
      return false;
    }
  }

  loginUser(loginDetails: LoginPayload | RememberMeLoginPayload): Observable<LoginResponse> {
    return this.http
      .post<{
        data: LoginResponse;
      }>(`${environment.base_url}admin/user/login`, loginDetails, {
        withCredentials: true
      })
      .pipe(map(res => res.data));
  }

  forgotPassword(email: ForgotPayload): Observable<CommonResponse> {
    return this.http.post<CommonResponse>(
      `${environment.base_url}admin/user/forgotPassword`,
      email
    );
  }

  verifyOtp(
    otp: VerifyOtpPayload,
    queryString: string
  ): Observable<VerifyOtpResponse> {
    return this.http
      .post<{
        data: VerifyOtpResponse;
      }>(`${environment.base_url}admin/user/verifyOtp?${queryString}`, otp)
      .pipe(map(res => res.data));
  }

  resetPassword(data: ResetPasswordPayload): Observable<CommonResponse> {
    return this.http
      .post<CommonResponse>(
        `${environment.base_url}admin/user/resetPassword`,
        data
      )
      .pipe(map(res => res));
  }

  getProfile(): Observable<LoginResponse> {
    return this.http
      .get<{ data: LoginResponse }>(`${environment.base_url}admin/user`)
      .pipe(map(res => res.data));
  }

  updateProfile(data: LoginResponse): Observable<LoginResponse> {
    return this.http
      .put<{
        data: LoginResponse;
      }>(`${environment.base_url}admin/user`, data)
      .pipe(map(res => res.data));
  }

  changePassword(data: ChangePasswordPayload): Observable<CommonResponse> {
    return this.http
      .post<CommonResponse>(
        `${environment.base_url}admin/user/changePassword`,
        data
      )
      .pipe(map(res => res));
  }

  logout(): Observable<CommonResponse> {
    return this.http.post<CommonResponse>(
      `${environment.base_url}admin/user/logout`,
      {},
      { withCredentials: true }
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logoutAlert(): Promise<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new Promise((resolve: any) => {
      Swal.fire({
        title: this._translateService.instant('AreYouSure'),
        text: this._translateService.instant('Youwillbeloggedout'),
        icon: 'warning',
        showCancelButton: true,
        cancelButtonText: this._translateService.instant('Cancel'),
        confirmButtonColor: '#1F9CBD',
        cancelButtonColor: '#000',
        confirmButtonText: this._translateService.instant('Logout')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }).then((result: any) => {
        return resolve(result);
      });
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  passwordChangedLogout(): Promise<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new Promise((resolve: any) => {
      Swal.fire({
        title: this._translateService.instant('PasswordUpdatedSuccessfully'),
        text: this._translateService.instant('Youwillbeloggedout'),
        icon: 'success'
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }).then((result: any) => {
        return resolve(result);
      });
    });
  }

  getReceivedNotificationList(
    query: string
  ): Observable<receivedNotificationList> {
    return this.http
      .get<{
        data: receivedNotificationList;
      }>(`${environment.base_url}admin/notifications/get?${query}`)
      .pipe(map(res => res.data));
  }

  invoicePayOut(payload: any): Observable<any> {
    return this.http.post<any>(
      `${environment.base_url}admin/payment/payInvoice`,
      payload
    );
  }

  getPaymentStatus(id: string): Observable<CommonResponse> {
    return this.http.get<CommonResponse>(
      `${environment.base_url}admin/payment/checkInvoiceStatus/${id}`      
    );
  }

  
  loginAsAdmin(data: any): Observable< {accessToken: string}> {
    return this.http
      .post<{
        data:  {accessToken: string};
      }>(`${environment.base_url}admin/tenant/generateTenantToken`, data)
      .pipe(map(res => res.data));
  }
}
